import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { projectsAPI, adminAPI } from '../services/api';
import { getAuth, hasPermission } from '../utils/auth';
import { t } from '../utils/i18n';

function Dashboard() {
    const [stats, setStats] = useState({
        totalProjects: 0,
        totalEntries: 0,
        confirmedEntries: 0,
        activeUsers: 0
    });
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [, forceUpdate] = useState(0);
    const authData = getAuth();
    // BUG FIX: user state-ը պետք է թարմանա localStorage-ից, երբ component-ը mount է լինում
    // useEffect-ում ստուգում ենք localStorage-ից թարմ user-ը և թարմացնում state-ը
    const [user, setUser] = useState(authData.user);
    const isAdmin = hasPermission('canManageUsers');
    
    // BUG FIX: Ստուգում ենք localStorage-ից user-ի փոփոխությունները և թարմացնում state-ը
    useEffect(() => {
        const currentAuth = getAuth();
        if (currentAuth.user && currentAuth.user.id !== user?.id) {
            setUser(currentAuth.user);
        }
    }, [user?.id]);
    
    // Debug: Log user and projects state
    useEffect(() => {
        console.log('Dashboard - User:', user);
        console.log('Dashboard - Projects state:', projects);
        console.log('Dashboard - Loading:', loading);
        console.log('Dashboard - Is Admin:', isAdmin);
    }, [user, projects, loading, isAdmin]);

    const loadData = useCallback(async () => {
        console.log('=== loadData START ===');
        // Get fresh user data from localStorage
        const currentAuth = getAuth();
        const currentUser = currentAuth.user || user;
        const currentIsAdmin = hasPermission('canManageUsers');
        
        console.log('User from state:', user);
        console.log('User from localStorage:', currentUser);
        console.log('Is Admin:', currentIsAdmin);
        
        try {
            setLoading(true);
            
            // Load stats (admin only)
            if (currentIsAdmin) {
                console.log('Loading stats for admin...');
                const statsRes = await adminAPI.getStats();
                if (statsRes.data.success) {
                    setStats(statsRes.data.stats);
                }
            }

            // Load user projects - ALWAYS load for all users
            console.log('Loading projects for user:', currentUser?.role);
            console.log('User ID:', currentUser?.id);
            const token = localStorage.getItem('token');
            console.log('Token exists:', !!token);
            console.log('Token value:', token ? token.substring(0, 20) + '...' : 'null');
            console.log('All localStorage keys:', Object.keys(localStorage));
            try {
                console.log('Calling projectsAPI.getAll()...');
                const projectsRes = await projectsAPI.getAll();
                console.log('=== Projects API Response ===');
                console.log('Full response:', projectsRes);
                console.log('Response status:', projectsRes?.status);
                console.log('Response data:', projectsRes?.data);
                console.log('Success:', projectsRes?.data?.success);
                console.log('Projects array:', projectsRes?.data?.projects);
                console.log('Projects array length:', projectsRes?.data?.projects?.length);
                
                if (projectsRes?.data?.success) {
                    const allProjects = projectsRes.data.projects || [];
                    console.log('✅ Loaded projects count:', allProjects.length);
                    console.log('✅ All projects:', JSON.stringify(allProjects, null, 2));
                    // For admin, show only first 5, for others show all
                    const projectsToShow = isAdmin ? allProjects.slice(0, 5) : allProjects;
                    console.log('✅ Projects to show:', JSON.stringify(projectsToShow, null, 2));
                    console.log('Setting projects state...');
                    setProjects(projectsToShow);
                    console.log('✅ Projects state set! Count:', projectsToShow.length);
                } else {
                    console.error('❌ Failed to load projects - no success flag');
                    console.error('Response:', projectsRes?.data);
                    setProjects([]);
                }
            } catch (err) {
                console.error('❌ Error loading projects:', err);
                console.error('Error response:', err.response);
                console.error('Error status:', err.response?.status);
                console.error('Error data:', err.response?.data);
                console.error('Error message:', err.message);
                
                // Don't set empty projects if it's an auth error - let API interceptor handle it
                // But don't crash the component
                if (err.response?.status === 401 || err.response?.status === 403) {
                    console.log('⚠️ Auth error in projects load - API interceptor will handle redirect');
                } else {
                    setProjects([]);
                }
            }
        } catch (error) {
            console.error('❌ Error loading dashboard:', error);
        } finally {
            console.log('=== loadData FINISHED, setting loading to false ===');
            setLoading(false);
        }
    }, [user, isAdmin]);

    useEffect(() => {
        console.log('=== Dashboard useEffect triggered ===');
        console.log('User:', user);
        console.log('User role:', user?.role);
        console.log('User ID:', user?.id);
        console.log('Is Admin:', isAdmin);
        
        // Always try to load data if user exists
        if (user && user.id) {
            console.log('User exists, calling loadData...');
            loadData();
        } else {
            console.log('No user found, checking localStorage...');
            // Try to get user from localStorage again
            const currentAuth = getAuth();
            if (currentAuth.user && currentAuth.user.id) {
                console.log('Found user in localStorage, calling loadData...');
                loadData();
            } else {
                console.error('❌ No user found in localStorage!');
            }
        }
    }, [user?.id, user?.role, loadData]);

    useEffect(() => {
        const handleLanguageChange = () => {
            forceUpdate(prev => prev + 1);
        };
        window.addEventListener('languageChanged', handleLanguageChange);
        return () => window.removeEventListener('languageChanged', handleLanguageChange);
    }, []);

    const translateRole = (role) => {
        return t(role) || role;
    };

    if (loading) {
        return (
            <Layout>
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <div className="loading" style={{ width: '40px', height: '40px', margin: '0 auto' }}></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="content-header">
                <h1 className="page-title">
                    {t('welcome')}, {user?.full_name || user?.username}!
                </h1>
                <p className="page-description">
                    {t('lastLogin')}: {user?.last_login ? new Date(user.last_login).toLocaleString('hy-AM') : t('never')} • {translateRole(user?.role)}
                </p>
            </div>

            {user?.role === 'admin' && (
                <div className="quick-actions" style={{ marginBottom: '32px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <Link to="/projects?action=new" className="btn btn-primary">
                        <i className="fas fa-plus"></i>
                        <span>{t('addNew')} {t('projects')}</span>
                    </Link>
                </div>
            )}

            {isAdmin && (
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon">
                            <i className="fas fa-project-diagram"></i>
                        </div>
                        <div className="stat-value">{stats.totalProjects}</div>
                        <div className="stat-label">{t('projects')}</div>
                    </div>
                    
                    <div className="stat-card">
                        <div className="stat-icon secondary">
                            <i className="fas fa-language"></i>
                        </div>
                        <div className="stat-value">{stats.totalEntries}</div>
                        <div className="stat-label">{t('translations')}</div>
                    </div>
                    
                    <div className="stat-card">
                        <div className="stat-icon accent">
                            <i className="fas fa-check-circle"></i>
                        </div>
                        <div className="stat-value">{stats.confirmedEntries}</div>
                        <div className="stat-label">{t('confirmed')}</div>
                    </div>
                    
                    <div className="stat-card">
                        <div className="stat-icon" style={{ background: 'rgba(96, 173, 155, 0.1)', color: '#60AD9B' }}>
                            <i className="fas fa-users"></i>
                        </div>
                        <div className="stat-value">{stats.activeUsers}</div>
                        <div className="stat-label">{t('active')} {t('admin')}</div>
                    </div>
                </div>
            )}

            <div className="table-container" style={{ marginTop: '32px' }}>
                {isAdmin ? (
                    <>
                        <div className="table-header">
                            <h2 className="table-title">{t('yourProjects')}</h2>
                            <Link to="/projects" className="btn btn-outline">
                                <i className="fas fa-eye"></i>
                                <span>{t('viewAll')}</span>
                            </Link>
                        </div>
                        
                        {projects.length > 0 ? (
                            <div className="table-responsive">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>{t('projects')}</th>
                                            <th>{t('active')}</th>
                                            <th>{t('pending')}</th>
                                            <th>{t('edit')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {projects.map((project) => (
                                            <tr key={project.id}>
                                                <td>
                                                    <strong>{project.name}</strong>
                                                    {project.description && (
                                                        <>
                                                            <br />
                                                            <small style={{ color: 'var(--gray-600)' }}>
                                                                {project.description.substring(0, 50)}...
                                                            </small>
                                                        </>
                                                    )}
                                                </td>
                                                <td>
                                                    <span className={`status-badge ${project.status === 'active' ? 'confirmed' : 'pending'}`}>
                                                        {project.status === 'active' ? t('active') : t('inactive')}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <div style={{ flex: 1, background: '#eee', height: '8px', borderRadius: '4px', overflow: 'hidden' }}>
                                                            <div 
                                                                style={{ 
                                                                    width: `${project.progress}%`, 
                                                                    height: '100%', 
                                                                    background: project.progress === 100 ? '#60AD9B' : '#CF9B06' 
                                                                }}
                                                            ></div>
                                                        </div>
                                                        <span style={{ fontSize: '12px', color: '#666' }}>{project.progress}%</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <Link 
                                                        to={`/translations/${project.id}`} 
                                                        className="btn btn-outline btn-sm"
                                                    >
                                                        <i className="fas fa-edit"></i>
                                                    </Link>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="empty-state">
                                <div className="empty-icon">
                                    <i className="fas fa-folder-open"></i>
                                </div>
                                <h3 className="empty-title">{t('noProjects')}</h3>
                                <p className="empty-description">{t('noProjectsDescription')}</p>
                                <Link to="/projects?action=new" className="btn btn-primary">
                                    <i className="fas fa-plus"></i>
                                    <span>{t('createFirst')}</span>
                                </Link>
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        {/* For facilitator and other roles - simple project list */}
                        {/* Debug info */}
                        {console.log('Rendering facilitator view - loading:', loading, 'projects:', projects, 'projects.length:', projects?.length)}
                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '40px' }}>
                                <div className="loading" style={{ width: '40px', height: '40px', margin: '0 auto' }}></div>
                                <p style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>{t('loading')}</p>
                            </div>
                        ) : projects && Array.isArray(projects) && projects.length > 0 ? (
                            <div style={{ display: 'grid', gap: '12px' }}>
                                {projects.map((project) => (
                                    <Link 
                                        key={project.id}
                                        to={`/translations/${project.id}`}
                                        style={{
                                            display: 'block',
                                            padding: '20px',
                                            background: 'var(--bg-color)',
                                            borderRadius: '8px',
                                            border: '1px solid var(--border-color)',
                                            textDecoration: 'none',
                                            color: 'var(--text-primary)',
                                            transition: 'all 0.2s',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = 'var(--bg-secondary)';
                                            e.currentTarget.style.borderColor = 'var(--brand-primary)';
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'var(--bg-color)';
                                            e.currentTarget.style.borderColor = 'var(--border-color)';
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                        }}
                                    >
                                        <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: 'var(--text-primary)' }}>
                                            {project.name}
                                        </h3>
                                        {project.description && (
                                            <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: 'var(--text-secondary)' }}>
                                                {project.description}
                                            </p>
                                        )}
                                    </Link>
                                ))}
                            </div>
                        ) : (
                            <div className="empty-state">
                                <div className="empty-icon">
                                    <i className="fas fa-folder-open"></i>
                                </div>
                                <h3 className="empty-title">{t('noProjects')}</h3>
                                <p className="empty-description">{t('noProjectsDescription')}</p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Layout>
    );
}

export default Dashboard;

